// utils/agentStatus.ts
// Translates Cortex agent stream events into language a client (attorney) reads.
//
// Every status event carries a machine-readable `status` code alongside Snowflake's
// own prose `message`. We key off the code — the prose is internal-facing and names
// Snowflake objects ("Running MEDICAL_RECORD_SEARCH_SERVICE"). Codes we haven't seen
// fall through to that prose, so a new agent capability degrades to today's behaviour
// instead of showing nothing.

/** Codes observed on response.status and response.tool_result.status events. */
const STATUS_LABELS: Record<string, string> = {
  planning: 'Working out how to answer',
  extracting_tool_calls: 'Preparing to search the records',
  executing_tools: 'Preparing to search the records',
  executing_tool: 'Searching the medical records',
  sending_search_results: 'Reading the matching records',
  reevaluating_plan: 'Looking for more detail',
  reasoning_agent_stop: 'Reviewing what was found',
  proceeding_to_answer: 'Writing the answer',
};

export const FIRST_STEP_LABEL = 'Reading your question';
export const THINKING_LABEL = 'Thinking';

/**
 * User-facing label for a status event, or null if we have nothing to show.
 * `message` is Snowflake's prose, used only as a fallback for unknown codes.
 */
export function statusLabel(status?: string, message?: string): string | null {
  if (status && STATUS_LABELS[status]) return STATUS_LABELS[status];
  return message || null;
}

/** The agent's own phrasing of what it is looking for, e.g. "chief complaints". */
export function searchQuery(event: any): string | null {
  const query = event?.input?.query;
  return typeof query === 'string' && query.trim() ? query.trim() : null;
}

/**
 * Turn a completed search into a plain-language count: how much was found, and
 * across how many documents. Returns null for events that carry no results.
 */
export function searchResultSummary(event: any): string | null {
  const results = event?.content?.[0]?.json?.search_results;
  if (!Array.isArray(results) || results.length === 0) return null;

  const docs = new Set(results.map((r: any) => r.doc_id).filter(Boolean));
  const passages = `${results.length} ${results.length === 1 ? 'excerpt' : 'excerpts'}`;
  if (docs.size === 0) return `Found ${passages}`;
  return `Found ${passages} in ${docs.size} ${docs.size === 1 ? 'document' : 'documents'}`;
}
